/**
 * The first-run demo, as a stacked deck and a running order.
 *
 * ## Why this is a deck and not a drawing
 *
 * An earlier version of this drew a little board out of DOM elements. It was
 * never going to be right: the real table is a shader — lit wells, foil on the
 * wilds, a streak border that flashes, cards with generated normal maps — and
 * every hour spent making CSS resemble that produced something that resembled
 * it slightly more and still did not match. Alignment, sizes and the wild's
 * foil were all wrong in ways that could only ever be chased, never fixed.
 *
 * So the demo is the game. `GameEngine` accepts a stacked deck, which is what
 * this file is, and the round is played on the real board by the real
 * choreography with the real effects. It is identical to the game because it
 * *is* the game — there is nothing left to keep in sync.
 *
 * ## The order, and why each card is where it is
 *
 * A streak is the thing that needed the most care. `applyOutcome` resets it on
 * *any* play that does not score, so clears have to be consecutive — and since
 * building a pile takes non-scoring cards, a demo that primed and cleared one
 * pile at a time could never get past the first level. The piles are therefore
 * all primed first and then cleared back to back:
 *
 *   7h  -> 0   a card is placed. Nothing happens. That is the whole move.
 *   10s -> 1   priming: 17, one short of 21
 *   7d  -> 1
 *   2c  -> 2   priming: four cards on 14, one short of five
 *   3d  -> 2
 *   4h  -> 2
 *   5s  -> 2
 *   10d -> 3   priming: 19, and nothing but a wild will save it
 *   9c  -> 3
 *   4c  -> 1   21 exactly. Clears. First of three in a row.
 *   6c  -> 2   five cards without going over. Second — the streak opens at 2x.
 *   js  -> 3   the wild, in foil, clearing a 19. Third — the streak climbs to 3x.
 *   10h -> 0   a card that clears nothing, so the streak dies here
 *   9h  -> 0   26. A bust, and one of the three gone.
 */

import type { CardId } from '@/game/types';

export interface DemoBeat {
  /** Pile to place the visible card on. */
  readonly pile: number;
  /** What to say while it happens, or null to leave the last line up. */
  readonly caption: string | null;
  /** Beat before the next card, in seconds. Longer where something lands. */
  readonly hold: number;
}

/**
 * The deck, in the order it is dealt.
 *
 * Only the first twelve are played; the rest are never reached and exist
 * because a deck is a deck. No wilds among the tail — a stray J on the stock
 * would light the wells at the wrong moment.
 */
export const DEMO_DECK: readonly CardId[] = buildDemoDeck();

/**
 * A full 52, scripted at the top.
 *
 * It has to be a whole deck, not just the twelve cards the walkthrough plays:
 * the HUD counts what is left, and a short deck would have the player's first
 * sight of the game be a card counter reading 23. The rest is the ordinary deck
 * with the scripted cards lifted out of it, and the wilds pushed to the back so
 * the card left face up at the end is an ordinary one — a jack sitting on the
 * stock would light all four wells while the player reads the last caption.
 */
function buildDemoDeck(): CardId[] {
  const scripted: CardId[] = [
    '7h',
    '10s', '7d',
    '2c', '3d', '4h', '5s',
    '10d', '9c',
    '4c', '6c', 'js',
    '10h', '9h',
  ];

  const ranks = ['a', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'j', 'q', 'k'];
  const rest: CardId[] = [];
  for (const suit of ['h', 's', 'c', 'd']) {
    for (const rank of ranks) {
      const id = `${rank}${suit}`;
      if (!scripted.includes(id)) rest.push(id);
    }
  }

  // Wilds last, so neither can become the face-up card while this is running.
  const wilds = rest.filter((id) => id === 'js' || id === 'jc');
  const plain = rest.filter((id) => id !== 'js' && id !== 'jc');
  return [...scripted, ...plain, ...wilds];
}

export const DEMO_BEATS: readonly DemoBeat[] = [
  { pile: 0, caption: 'Tap a pile to put the card there. That is the whole move.', hold: 1.4 },

  { pile: 1, caption: 'Piles add up as you go. Get one to exactly 21 and it clears.', hold: 0.9 },
  { pile: 1, caption: null, hold: 0.8 },

  { pile: 2, caption: 'Five cards on a pile without going over clears it too.', hold: 0.7 },
  { pile: 2, caption: null, hold: 0.6 },
  { pile: 2, caption: null, hold: 0.6 },
  { pile: 2, caption: null, hold: 0.7 },

  { pile: 3, caption: 'This one is stuck on 19. Only a wild can save it.', hold: 0.8 },
  { pile: 3, caption: null, hold: 1.1 },

  // Three clears, back to back — which is the only way a streak survives.
  { pile: 1, caption: 'Twenty-one exactly.', hold: 2.6 },
  { pile: 2, caption: 'Five cards — and two clears in a row opens a streak. 2×.', hold: 3.2 },
  { pile: 3, caption: 'The wild clears any pile whatever the total. Three in a row: 3×.', hold: 3.8 },

  { pile: 0, caption: 'Play a card that clears nothing and the streak is gone.', hold: 2.0 },
  { pile: 0, caption: 'Go over 21 and the pile clears for nothing, and costs a bust.', hold: 3.2 },
];
